<template>
    <div class="stats">
        <div v-if="!transactions.length" class="stats-card stats-welcome">
            <v-icon size="44" color="rgba(27,47,90,0.35)">mdi-chart-box-outline</v-icon>
            <p>
                Εδώ θα βλέπετε πόσες αποδείξεις έχετε καταχωρίσει, το ποσό τους και τις επιχειρήσεις που επισκέπτεστε πιο συχνά.
            </p>
            <v-btn color="#1b2f5a" dark depressed :to="{ name: 'QR' }">
                <v-icon left small>mdi-qrcode-scan</v-icon>Σκανάρετε την πρώτη σας απόδειξη
            </v-btn>
        </div>

        <template v-else>
            <div class="stats-filters">
                <div class="stats-presets" role="group" aria-label="Περίοδος">
                    <button
                        v-for="preset in presets"
                        :key="preset.key"
                        type="button"
                        class="stats-preset"
                        :class="{ 'stats-preset--active': period === preset.key }"
                        @click="period = preset.key"
                    >
                        {{ preset.label }}
                    </button>
                </div>
            </div>

            <div class="stats-kpis">
                <div class="stats-kpi">
                    <div class="stats-kpi__label"><v-icon small>mdi-receipt-text-outline</v-icon>Αποδείξεις</div>
                    <div class="stats-kpi__value">{{ number(summary.count) }}</div>
                    <div class="stats-kpi__note">{{ number(summary.completed) }} επιβεβαιωμένες</div>
                </div>
                <div class="stats-kpi stats-kpi--accent">
                    <div class="stats-kpi__label"><v-icon small>mdi-cash-check</v-icon>Επιβεβαιωμένο ποσό</div>
                    <div class="stats-kpi__value">{{ money(summary.amount) }}</div>
                    <div class="stats-kpi__note">μέση απόδειξη {{ money(summary.average) }}</div>
                </div>
                <div class="stats-kpi stats-kpi--pending">
                    <div class="stats-kpi__label"><v-icon small>mdi-timer-sand</v-icon>Σε εκκρεμότητα ΑΑΔΕ</div>
                    <div class="stats-kpi__value">{{ money(summary.pendingAmount) }}</div>
                    <div class="stats-kpi__note">{{ number(summary.pending) }} αποδείξεις</div>
                </div>
                <div class="stats-kpi">
                    <div class="stats-kpi__label"><v-icon small>mdi-storefront-outline</v-icon>Επιχειρήσεις</div>
                    <div class="stats-kpi__value">{{ number(summary.partners) }}</div>
                    <div class="stats-kpi__note">{{ memberSince ? `μέλος από ${memberSince}` : 'που επισκεφθήκατε' }}</div>
                </div>
            </div>

            <section class="stats-card">
                <div class="stats-card__head">
                    <h3>Ανά μήνα</h3>
                    <div class="stats-toggle" role="group" aria-label="Μέγεθος">
                        <button type="button" :class="{ 'stats-toggle--active': metric === 'count' }" @click="metric = 'count'">Αποδείξεις</button>
                        <button type="button" :class="{ 'stats-toggle--active': metric === 'amount' }" @click="metric = 'amount'">Ποσό</button>
                    </div>
                </div>
                <div v-if="monthlyMax" ref="chart" class="stats-chart">
                    <div v-for="month in monthly" :key="month.key" class="stats-chart__col" :title="monthTitle(month)">
                        <span class="stats-chart__value">{{ month[metric] ? (metric === 'amount' ? shortMoney(month.amount) : month.count) : '' }}</span>
                        <div class="stats-chart__bar" :style="{ height: barHeight(month[metric]) }" />
                        <span class="stats-chart__label">{{ monthLabel(month) }}</span>
                    </div>
                </div>
                <p v-else class="stats-empty">Δεν υπάρχουν {{ metric === 'amount' ? 'επιβεβαιωμένα ποσά' : 'αποδείξεις' }} σε αυτή την περίοδο.</p>
            </section>

            <section class="stats-card">
                <div class="stats-card__head"><h3>Οι επιχειρήσεις σας</h3></div>
                <ul v-if="partners.length" class="stats-ranking">
                    <li v-for="item in partners" :key="item.title">
                        <div class="stats-ranking__row">
                            <span class="stats-ranking__title">{{ item.title }}</span>
                            <span class="stats-ranking__value">{{ item.count }} · {{ money(item.amount) }}</span>
                        </div>
                        <div class="stats-ranking__track">
                            <div class="stats-ranking__fill" :style="{ width: share(item.count) }" />
                        </div>
                    </li>
                </ul>
                <p v-else class="stats-empty">Καμία απόδειξη από συνεργαζόμενη επιχείρηση σε αυτή την περίοδο.</p>
                <div class="stats-more">
                    <v-btn small outlined color="#1b2f5a" to="/Partners">
                        <v-icon left small>mdi-tag-outline</v-icon>Δείτε όλες τις εκπτώσεις
                    </v-btn>
                </div>
            </section>

            <p class="stats-footnote">
                Οι αποδείξεις μετρώνται με την ημερομηνία τους. Το επιβεβαιωμένο ποσό περιλαμβάνει μόνο αποδείξεις που έχει ελέγξει η ΑΑΔΕ.
            </p>
        </template>
    </div>
</template>

<script>
// A card member's own figures, worked out from the transactions the profile already loaded
export default {
    name: 'MemberStats',
    props: {
        transactions: { type: Array, default: () => [] },
        // member.createdAt from the card login
        createdAt: { type: [String, Object], default: null }
    },
    data: () => ({
        presets: [
            { key: 'year', label: 'Φέτος' },
            { key: '12m', label: '12 μήνες' },
            { key: 'all', label: 'Όλα' }
        ],
        period: '12m',
        metric: 'count'
    }),
    computed: {
        rows() {
            return this.transactions.map(transaction => {
                const receipt = transaction.receipt || {}
                const completed = !!(receipt.scanCompleted !== undefined && receipt.scanCompleted !== null ? receipt.scanCompleted : transaction.scanCompleted)
                const total = Number(receipt.total)
                const hasTotal = receipt.total !== null && receipt.total !== undefined && !Number.isNaN(total)
                const day = this.toDate(receipt.date) || this.toDate(transaction.createdAt)
                return {
                    day,
                    completed,
                    amount: completed && hasTotal ? total : 0,
                    pendingAmount: !completed && hasTotal ? total : 0,
                    partner: transaction.partner ? transaction.partner.title : null
                }
            }).filter(row => row.day)
        },
        periodStart() {
            const now = new Date()
            if (this.period === 'year') {
                return new Date(now.getFullYear(), 0, 1)
            }
            if (this.period === '12m') {
                return new Date(now.getFullYear(), now.getMonth() - 11, 1)
            }
            return null
        },
        inPeriod() {
            return this.rows.filter(row => !this.periodStart || row.day >= this.periodStart)
        },
        summary() {
            const completed = this.inPeriod.filter(row => row.completed)
            const amount = completed.reduce((sum, row) => sum + row.amount, 0)
            return {
                count: this.inPeriod.length,
                completed: completed.length,
                pending: this.inPeriod.length - completed.length,
                amount,
                pendingAmount: this.inPeriod.reduce((sum, row) => sum + row.pendingAmount, 0),
                average: completed.length ? amount / completed.length : 0,
                partners: new Set(this.inPeriod.map(row => row.partner).filter(Boolean)).size
            }
        },
        monthly() {
            const now = new Date()
            const first = this.periodStart
                || new Date(Math.min(...this.rows.map(row => row.day.getTime())))
            const months = []
            for (let month = new Date(first.getFullYear(), first.getMonth(), 1); month <= now; month = new Date(month.getFullYear(), month.getMonth() + 1, 1)) {
                months.push({ key: `${month.getFullYear()}-${month.getMonth()}`, date: month, count: 0, amount: 0 })
            }
            const byKey = Object.fromEntries(months.map(month => [month.key, month]))
            this.inPeriod.forEach(row => {
                const month = byKey[`${row.day.getFullYear()}-${row.day.getMonth()}`]
                if (month) {
                    month.count++
                    month.amount += row.amount
                }
            })
            return months
        },
        monthlyMax() {
            return Math.max(0, ...this.monthly.map(month => month[this.metric]))
        },
        partners() {
            const groups = {}
            this.inPeriod.forEach(row => {
                if (!row.partner) {
                    return
                }
                groups[row.partner] = groups[row.partner] || { title: row.partner, count: 0, amount: 0 }
                groups[row.partner].count++
                groups[row.partner].amount += row.amount
            })
            return Object.values(groups).sort((a, b) => b.count - a.count || b.amount - a.amount).slice(0, 6)
        },
        memberSince() {
            const date = this.toDate(this.createdAt)
            return date ? date.toLocaleDateString('el-GR', { month: 'long', year: 'numeric' }) : ''
        }
    },
    watch: {
        period() {
            this.showLatestMonths()
        },
        metric() {
            this.showLatestMonths()
        }
    },
    mounted() {
        this.showLatestMonths()
    },
    methods: {
        toDate(value) {
            if (!value) {
                return null
            }
            const date = new Date(typeof value === 'object' && value.date ? value.date.replace(' ', 'T') : value)
            return Number.isNaN(date.getTime()) ? null : date
        },
        // long periods scroll sideways: show the latest month that has figures
        showLatestMonths() {
            this.$nextTick(() => {
                const chart = this.$refs.chart
                if (!chart) {
                    return
                }
                let last = this.monthly.length - 1
                while (last > 0 && !this.monthly[last][this.metric]) {
                    last--
                }
                const column = chart.children[last]
                chart.scrollLeft = column ? column.offsetLeft + column.offsetWidth - chart.clientWidth + 24 : chart.scrollWidth
            })
        },
        number(value) {
            return Number(value || 0).toLocaleString('el-GR')
        },
        money(value) {
            return Number(value || 0).toLocaleString('el-GR', { style: 'currency', currency: 'EUR' })
        },
        shortMoney(value) {
            return value >= 1000 ? `${(value / 1000).toLocaleString('el-GR', { maximumFractionDigits: 1 })}k€` : `${Math.round(value)}€`
        },
        monthLabel(month) {
            const label = month.date.toLocaleDateString('el-GR', { month: 'short' })
            return month.date.getMonth() === 0 || month === this.monthly[0] ? `${label} ${String(month.date.getFullYear()).slice(2)}` : label
        },
        monthTitle(month) {
            const name = month.date.toLocaleDateString('el-GR', { month: 'long', year: 'numeric' })
            return `${name}: ${month.count} αποδείξεις, ${this.money(month.amount)}`
        },
        barHeight(value) {
            return this.monthlyMax ? `${Math.max(value ? 4 : 0, Math.round((value / this.monthlyMax) * 100))}%` : '0%'
        },
        share(value) {
            const max = this.partners.length ? this.partners[0].count : 0
            return max ? `${Math.max(4, Math.round((value / max) * 100))}%` : '0%'
        }
    }
}
</script>

<style scoped src="@/styles/stats.css"></style>
<style scoped>
.stats-welcome {
    padding: 32px 20px;
    text-align: center;
}

.stats-welcome p {
    max-width: 440px;
    margin: 10px auto 18px;
    color: rgba(27, 47, 90, 0.75);
}

.stats-more {
    margin-top: 16px;
}
</style>
